import { useProductPreviewData } from '../../hooks/useProductPreviewData.js'

import { Button }        from '../Button/index.js'
import { DataGrid }      from '../DataGrid/index.js'
import { StatusBadge }   from '../StatusBadge/index.js'
import { UnitPlanThumb } from '../UnitPlanThumb/UnitPlanThumb.jsx'
import { OfferBadge }    from '../OfferBadge/OfferBadge.jsx'

import styles from './ProductPreview.module.scss'

/**
 * ProductPreview — always-horizontal preview card for a product.
 *
 * Two-column layout:
 * - Left:  header (id) + DataGrid (status, area, rooms, tier) + optional CTA
 * - Right: UnitPlanThumb (optional, controlled by `showPlan`)
 *
 * Reusable across contexts: Popover (desktop), BottomSheet (mobile), ModelView, VisualizationView, TiersView.
 * Zero positioning logic — the container (Popover / BottomSheet) owns that.
 *
 * @param {object}   props
 * @param {object}   props.product          - Product data object
 * @param {(uuid: string) => void} [props.onAction] - CTA click handler
 * @param {string}   [props.className]      - Extra class on root
 */
export function ProductPreview({
  className,
  onAction,
  product,
}) {

  const deferPlanMs = 90

  const {
    getProductsCategoryString,
    gridItems,
    isPlanMounted,
    isOnOffer,
    showPlanThumb,
    logoSrc,
    planVisible,
    projectDataUrl
  } = useProductPreviewData({
    product,
    deferPlanMs,
  })
  
  if (!product) return null

  const rootClass = [styles.holder, className].filter(Boolean).join(' ')
  
  return (
    <div className={rootClass}>
      {/* ── Left column: data ── */}
      <div>
        <div className={styles.info}>
          <div className={styles.header}>
            {isOnOffer && (
              <OfferBadge size="small"/>
            )}
            <StatusBadge status={product.status} size="small" />
          </div>
          <DataGrid 
            size="small" 
            columns={3} 
            items={gridItems}
          />
        </div>

          <div className={styles.action}>
            <Button
              cta
              type="button"
              kind="wide"
              size="large"
              onClick={() => onAction()}
            >
              {getProductsCategoryString(
                'open', 
                product.product_category, 
                { id: product.id_original ?? product.number ?? product.id_cad }
              )}
            </Button>
          </div>
      </div>

      {/* ── Right column: plan thumbnail ── */}
      {showPlanThumb && projectDataUrl && (
        <div className={styles.thumb}>
          {isPlanMounted
            ? (
              <UnitPlanThumb
                product={product}
                projectDataUrl={projectDataUrl}
                logoSrc={logoSrc}
                planVisible={planVisible}
              />
            ) : (
              <div className={styles.thumbPlaceholder} aria-hidden="true" />
            )}
        </div>
      )}
    </div>
  )
}
